import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  PageSettingsModel, InfiniteScrollSettingsModel, GridComponent, GridModule,
  SortService, InfiniteScrollService, PageService, SearchService
} from '@syncfusion/ej2-angular-grids';
import { ToastrService } from 'ngx-toastr';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgSelectModule } from '@ng-select/ng-select';
import { dmsUtility } from '../../dmsUtility';
import { TimeZoneDMSDataPipe } from 'src/app/shared/pipes/timezone-moment-dms.pipe';
import { DmsService } from '../../dms.service';
import { APP_CONSTANTS } from 'src/app/Models/SharePointPracticeModel';

@Component({
  selector: 'app-dms-folder-location',
  standalone: true,
  imports: [CommonModule, FormsModule, GridModule, MatButtonModule, MatTooltipModule, NgSelectModule, TimeZoneDMSDataPipe],
  templateUrl: './dms-folder-location.component.html',
  styleUrls: ['./dms-folder-location.component.scss'],
  providers: [SortService, InfiniteScrollService, PageService, SearchService]
})

export class DmsFolderLocationComponent extends dmsUtility implements OnInit, OnDestroy {
  @Input() ClientId: number = 0;
  @Input() currentFolderLocId: any;
  @Output() outputParameter: EventEmitter<any> = new EventEmitter<any>();
  @Output() sendFolderLocation: EventEmitter<any> = new EventEmitter<any>();
  unsubscribe = new Subject<void>();

  folders: any[];
  breadcrumbData: any[];
  groupName: string;
  destinationFolderId: any = null;
  @ViewChild('grid', { static: false })

  public grid: GridComponent;
  public pageSettings: Object;
  public options: PageSettingsModel;
  public initialPage?: PageSettingsModel;
  public infiniteOptions: InfiniteScrollSettingsModel;
  folderlocationObj: { locationPath: string; locationFolderId: any; rootFolderName: any; };

  bsModalRef: BsModalRef;
  @ViewChild('createFYFolderPopup', { static: false }) createFYFolderPopup: any;
  financialYearFolderData: any = {};

  constructor(toastr: ToastrService, _dmsService: DmsService, private router: Router, private modalService: BsModalService) {super(_dmsService, toastr); }

  ngOnInit(): void {
    this.initializeFYFolderConstants();

    if (!this.currentFolderLocId) {
      this.GetBreadcrumHomeData(this.ClientId);
    } else {
      this.breadcrumbData = [];
      this.folderPath(this.currentFolderLocId);
    }
  }

  initializeFYFolderConstants(canReset: boolean = true) {
    this.financialYearFolderData = { ...this.financialYearFolderData, fyFolderList: [], selectedFyFolderId: null };
    if (canReset) {
      this.financialYearFolderData.siteId = null;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  closePopUp() {
    this.initializeFYFolderConstants();
    this.outputParameter.emit();
  }

  GetBreadcrumHomeData(clientId: any) {
    this._dmsService.GetBreadcrumHomeData(clientId).pipe(takeUntil(this.unsubscribe)).subscribe((res: any) => {
      if (res.statusCode == 200) {
        const allFolders = JSON.parse(res.httpResponse);
        this.folders = allFolders.filter((folder: any) => folder.ClientId === Number(clientId));
        this.breadcrumbData = [];

        if (this.folders.length > 0) {
          this.groupName = this.folders[0].GroupName?.trim() ? this.folders[0].GroupName : this.folders[0].FName;
        } else {
          this.groupName = ''; // or any default value you want
        }
      }
      if (res.statusCode == 204) {
        this.breadcrumbData = [];
        this.folders = [];
        this.GetClientDocumentFolderLocation(clientId, null);
      }

    }, (error: any) => {
      let err: string = '';
      if (error instanceof HttpErrorResponse) {
        err = error.error.message ?? error.error.title;
        if (error.status === 401) {
          this.router.navigate(["/dms/auth"])
            .then(() => {
              this.toastr.error("Please click the Authorise button to re-establish your connection", 'Sharepoint Connection');
            })
            .catch((error) => {
              this.toastr.error(err, 'Sharepoint Connection');
            });
        }
        else if (error.status === 404) {
          this.toastr.error(err);
        }
        else if (error.status === 400) {
          this.toastr.error(err);
        }
      }
      else {
        this.toastr.error(err);
      }
    });
  }

  GetClientDocumentFolderLocation(clientId: any, folderId: any) {
    this.folders = [];
    this._dmsService.GetClientDocumentFolderLocation(clientId, folderId).pipe(takeUntil(this.unsubscribe)).subscribe((res: any) => {
      if (res?.statusCode == 200) {
        this.folders = JSON.parse(res.httpResponse);
        if (this.folders.length > 0 && folderId !== null) {
          this.GetBreadcrumbs(folderId);
        } else if(((this.breadcrumbData || []).length === 0)) {
          if(this.folders?.length > 0) {
            const rootFolderData: Folder = {
              Id: this.folders[0]?.ParentId,
              FolderName: this.folders[0]?.ClientName,
              SiteId: this.folders[0]?.SPSiteId,
              ParentId: APP_CONSTANTS.EMPTY_GUID
            }
            this.breadcrumbData.push(rootFolderData);
            this.financialYearFolderData.siteId = this.folders[0]?.SPSiteId || 0;
          } else {
            this.toastr.warning('Failed to fetch client root folders');
          }
        }
      } else if (res?.statusCode == 204) {
        this.GetBreadcrumbs(folderId);
      }
    }, (error: any) => {
      let err: string = '';
      if (error instanceof HttpErrorResponse) {
        err = error.error.message ?? error.error.title;
        if (error.status === 401) {
          this.toastr.error(`The connection with Microsoft-SharePoint has been lost.`, 'Sharepoint Document Connection');
        }
        else if (error.status === 404) {
          this.toastr.error(err, 'SharePoint Document');
        }
        else if (error.status === 400) {
          this.toastr.error(err, 'SharePoint Document');
        }
      }
      else {
        this.toastr.error(err, 'SharePoint Document');
      }
    });
  }

  GetBreadcrumbs(folderId: any) {
    this._dmsService.GetBreadcrumbs(folderId).pipe(takeUntil(this.unsubscribe)).subscribe(
      (res: any) => {
        if (res.statusCode == 200) {
          this.breadcrumbData = JSON.parse(res.httpResponse);
          this.financialYearFolderData.siteId = this.breadcrumbData[0]?.SiteId || 0;
        }
        if (res.statusCode == 204) {
          this.toastr.warning(res.message);
        }
      },
      (error: any) => {
        let err: string = '';
        if (error instanceof HttpErrorResponse) {
          err = error.error.message ?? error.error.title;
          if (error.status === 401 || error.status === 400) {
            this.toastr.error(err, 'SharePoint Connection');
          }
          else if (error.status === 404) {
            this.toastr.error(err, 'SharePoint Connection');
          }
        }
        else {
          this.toastr.error(err, 'SharePoint Connection');
        }
      }
    );
  }

  folderPath(folderId: any) {
    this.destinationFolderId = folderId;
    this.GetClientDocumentFolderLocation(this.ClientId, folderId);
  }


  search() {
    const searchText: string = (document.getElementsByClassName('searchtext')[0] as any).value;
    this.grid.search(searchText);
  }

  searchKyePress() {
    const searchText: string = (document.getElementsByClassName('searchtext')[0] as any).value;
    if (searchText.length == 0) {
      this.search();
    }
  }

  addFolderLocation() {
    const { breadcrumb, rootFolderName, clientName } = this.buildBreadcrumbInfo(this.destinationFolderId.toLowerCase(),this.breadcrumbData);
    this.folderlocationObj = {
      locationPath: clientName +' / '+ breadcrumb,
      locationFolderId: this.destinationFolderId,
      rootFolderName : rootFolderName
    }

    this.sendFolderLocation.emit(this.folderlocationObj);
  }

  buildBreadcrumbPaths(breadcrumbData: Folder[]): string {
    const folderMap = new Map<string, Folder>();
    const paths: string[] = [];

    // Map folders by their Id for quick lookup
    breadcrumbData.forEach(folder => folderMap.set(folder.Id, folder));

    // Function to recursively build the path
    const getPath = (folderId: string): string => {
      const folder = folderMap.get(folderId);
      if (!folder) {
        return '';
      }
      if (folder.ParentId === "00000000-0000-0000-0000-000000000000") {
        return `/${folder.FolderName}`;
      }
      return `${getPath(folder.ParentId)}/${folder.FolderName}`;
    };

    // Build paths for each folder in the data
    breadcrumbData.forEach(folder => {
      const fullPath = getPath(folder.Id);
      paths.push(fullPath);
    });

    return paths[paths.length - 1] ? ("Clients" + paths[paths.length - 1]) : "";
  }

  homeBreadCrumbClicked() {
    this.currentFolderLocId = null;
    this.ngOnInit();
  }

  onFolderLocationGridDataBound(): void {
    (this.grid as any).hideScroll();
  }

  // open create FY folder popup
  openCreateFYFolderPopup() {
    this.initializeFYFolderConstants(false);
    this.getFYFolderList();
    this.bsModalRef = this.modalService.show(this.createFYFolderPopup, { class: `modal-dialog-centered cmn-modal-dsgn create-fyfolder-modal`, backdrop: 'static' });
  }

  // close create FY folder popup
  closeCreateFYFolderPopup() {
    this.initializeFYFolderConstants(false);
    this.bsModalRef?.hide();
  }

  getFYFolderList() {
    const rootFolderId = this.breadcrumbData[0]?.Id || null;
    this._dmsService.getFYFolderListForClientRootFolder(rootFolderId).pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (res: any) => {
        if (res.statusCode == 200) {
          let fyFolderList = (res?.httpResponse || [])?.map((fy: any) => {
            return { fyId: fy, fyName: fy };
          });
          this.financialYearFolderData.fyFolderList = fyFolderList;
        } else {
          if (res?.isSuccess && res?.message == "No financial year options available") {
            this.toastr.info("No folders available to create. All eligible year folders already exist.");
          } else {
            const errorMessage = res?.message || 'Failed to fetch financial year folder list.';
            this.toastr.warning(errorMessage);
          }
        }
      },
      error: (error: any) => {
        let err: string = error?.error?.message || error?.error?.title || 'An error occurred while fetching financial year folder list.';
        this.toastr.error(err);
      }
    });
  }

  createFYFolder() {
    const payload = this.getPayloadForCreateFYFolder();
    
    this._dmsService.CreateChildFolder(payload).pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (res: any) => {
        if (res.statusCode == 200) {
          this.toastr.success(res?.message || 'Financial year folder created successfully.');
          this.closeCreateFYFolderPopup();
          this.GetClientDocumentFolderLocation(this.ClientId, this.breadcrumbData[0]?.Id || null);
        } else {
          const errorMessage = res.message || 'Failed to create financial year folder.';
          this.toastr.warning(errorMessage);
        }
      },
      error: (error: any) => {
        let err: string = error?.error?.message || error?.error?.title || 'An error occurred while creating financial year folder.';
        this.toastr.error(err);
      }
    });
  }

  getPayloadForCreateFYFolder() {
    return {
      metadataValues: "[]",
      clientId: this.ClientId,
      parentFolderId: this.breadcrumbData[0]?.Id || "",
      folderName: String(this.financialYearFolderData?.selectedFyFolderId) || "",
      siteID: this.financialYearFolderData?.siteId || 0
    };
  }

}
export interface Folder {
  Id: string;
  FolderName: string;
  SiteId: number;
  ParentId: string;
}